import React, { useEffect, useState } from 'react';
import { NotificationManager } from 'react-notifications';
import { Quantum } from 'ldrs/react';
import 'ldrs/react/Quantum.css';
import DashboardFooter from '../components/DashboardFooter';
import Select from "react-select";

const Managepartner = () => {
    const [loading, setLoading] = useState(false);
    const [employeesList, setEmployeesList] = useState([]);
    const [searchFilters, setSearchFilters] = useState({ name: '', role: '', email: '' });
    const [partnerMappings, setPartnerMappings] = useState([]);
    const [expandedPartners, setExpandedPartners] = useState({});
    const [editingMappingId, setEditingMappingId] = useState(null); // tracks if we're in update mode

    const emptyForm = { partner: null, employees: [] };
    const [formData, setFormData] = useState(emptyForm);

    const token = localStorage.getItem('token');

    /* ─── Fetch all users ─────────────────────────────────────── */
    const fetchAllEmployees = async () => {
        try {
            const filters = Object.fromEntries(
                Object.entries(searchFilters).filter(([, v]) => v)
            );
            const res = await fetch(`${process.env.REACT_APP_HOST_API_URL}/app/all-users/`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(filters),
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setEmployeesList(Array.isArray(data.data) ? data.data : []);
        } catch (err) {
            console.error('User list fetch error:', err);
        }
    };

    /* ─── Fetch partner mappings ──────────────────────────────── */
    const fetchPartnerMappings = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_HOST_API_URL}/admin-user/partner-employee-mappings/`,
                {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                }
            );
            if (!res.ok) throw new Error('Failed to fetch partner mappings');
            const data = await res.json();
            setPartnerMappings(Array.isArray(data) ? data : data.data || []);
        } catch (err) {
            console.error('Partner mapping fetch error:', err);
            NotificationManager.error('Failed to load partner mappings');
        }
    };

    useEffect(() => {
        fetchAllEmployees();
        fetchPartnerMappings();
    }, []);

    /* ─── Helpers ─────────────────────────────────────────────── */
    const handleSearchChange = (e) =>
        setSearchFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const resetForm = () => {
        setFormData(emptyForm);
        setEditingMappingId(null);
    };

    const togglePartner = (partnerId) =>
        setExpandedPartners((prev) => ({ ...prev, [partnerId]: !prev[partnerId] }));

    const partnerOptions = employeesList
        .filter((u) => u.role?.toLowerCase() === 'partner')
        .map((u) => ({ value: u.id, label: `${u.full_name} (${u.username})` }));

    // const employeeOptions = employeesList
    //     .filter((u) => u.role?.toLowerCase() === 'employee')
    //     .map((u) => ({ value: u.id, label: `${u.full_name} (${u.username})` }));

    const employeeOptions = employeesList
    .filter((u) => u.role?.toLowerCase() === 'employee' && u.is_active === true)
    .map((u) => ({ value: u.id, label: `${u.full_name} (${u.username})` }));

    
    /* ─── Group mappings by partner ───────────────────────────── */
    const groupedByPartner = partnerMappings.reduce((acc, mapping) => {
        const key = mapping.partner;
        if (!acc[key]) {
            acc[key] = {
                partner_id: mapping.partner,
                partner_full_name: mapping.partner_full_name,
                partner_username: mapping.partner_username,
                partner_email: mapping.partner_email,
                mapping_id: mapping.id, // first mapping id for PATCH
                employees: [],
            };
        }
        acc[key].employees.push({
            id: mapping.employee,
            full_name: mapping.employee_full_name,
            username: mapping.employee_username,
            email: mapping.employee_email,
            status: mapping.status,
            mapped_on: mapping.created_at,
        });
        return acc;
    }, {});

    const partnerGroups = Object.values(groupedByPartner);

    /* ─── Click partner card → prefill form ───────────────────── */
    const handlePartnerCardClick = (emp) => {
        const existingGroup = groupedByPartner[emp.id];
        if (existingGroup) {
            // Partner already has mappings → prefill and switch to update mode
            setFormData({
                partner: emp.id,
                employees: existingGroup.employees.map((e) => e.id),
            });
            setEditingMappingId(existingGroup.mapping_id);
        } else {
            // No existing mapping → just select partner, clear employees
            setFormData({ partner: emp.id, employees: [] });
            setEditingMappingId(null);
        }
        // Scroll to form smoothly
        document.getElementById('mapping-form-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    /* ─── Submit (POST or PATCH) ──────────────────────────────── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.partner) { NotificationManager.error('Please select a Partner'); return; }
        if (formData.employees.length === 0) { NotificationManager.error('Please select at least one Employee'); return; }

        const isUpdate = !!editingMappingId;
        const url = isUpdate
            ? `${process.env.REACT_APP_HOST_API_URL}/admin-user/partner-employee-mappings/${editingMappingId}/`
            : `${process.env.REACT_APP_HOST_API_URL}/admin-user/partner-employee-mappings/`;
        const method = isUpdate ? 'PATCH' : 'POST';

        try {
            setLoading(true);
            const res = await fetch(url, {
                method,
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ partner: formData.partner, employees: formData.employees }),
            });
            const text = await res.text();
            let data = {};
            try { data = JSON.parse(text); } catch (_) {}
            if (!res.ok) throw new Error(data.message || `Server error ${res.status}`);
            NotificationManager.success(isUpdate ? 'Mapping updated successfully' : 'Employees mapped to partner successfully');
            resetForm();
            fetchPartnerMappings();
        } catch (err) {
            NotificationManager.error(err.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    /* ─── Render ──────────────────────────────────────────────── */
    if (loading)
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 700 }}>
                <Quantum size="45" speed="1.75" color="blue" />
            </div>
        );

    return (
        <div className="d-flex flex-column min-vh-100 pb-2 px-3">
            <div className="flex-grow-1">
                <div className="container-fluid pb-2 px-2 px-md-4" style={{ paddingTop: 20 }}>

                    {/* ── Page header ── */}
                    <div
                        className="row mb-4 shadow-sm border-bottom sticky-top py-3 px-md-3 px-2 rounded"
                        style={{ zIndex: 1050, background: 'linear-gradient(135deg, #0564bf, #89CFF0)' }}
                    >
                        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h3 className="text-light mb-1">Manage Partner</h3>
                                <p className="text-light mb-0">Assign employees to a partner</p>
                            </div>
                        </div>
                    </div>

                    {/* ── Form + Partner list row ── */}
                    <div className="row">

                        {/* ── Left: Form ── */}
                        <div className="col-12 col-lg-8 mb-4">
                            <div id="mapping-form-card" className="border rounded p-4 bg-white shadow-sm">

                                {/* Form header — changes based on mode */}
                                <div className="text-center mb-4">
                                    <h4 style={{ color: '#0564bf' }}>
                                        {editingMappingId
                                            ? <><i className="fas fa-edit me-2"></i>Update Employee Mapping</>
                                            : <><i className="fas fa-link me-2"></i>Map Employees to Partner</>
                                        }
                                    </h4>
                                    
                                </div>

                                <form onSubmit={handleSubmit}>
                                    <div className="row g-4">
                                        <div className="col-12">
                                            <label className="form-label fw-semibold text-muted">
                                                Select Partner <span className="text-danger">*</span>
                                            </label>
                                            <Select
                                                options={partnerOptions}
                                                value={partnerOptions.find((o) => o.value === formData.partner) || null}
                                                onChange={(selected) => {
                                                    const newPartnerId = selected ? selected.value : null;
                                                    const existingGroup = newPartnerId ? groupedByPartner[newPartnerId] : null;
                                                    setFormData({
                                                        partner: newPartnerId,
                                                        employees: existingGroup ? existingGroup.employees.map((e) => e.id) : [],
                                                    });
                                                    setEditingMappingId(existingGroup ? existingGroup.mapping_id : null);
                                                }}
                                                placeholder="Search and select a partner..."
                                                isClearable
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold text-muted">
                                                Select Employees <span className="text-danger">*</span>
                                            </label>
                                            <Select
                                                isMulti
                                                options={employeeOptions}
                                                value={employeeOptions.filter((o) => formData.employees.includes(o.value))}
                                                onChange={(selected) =>
                                                    setFormData((prev) => ({ ...prev, employees: selected ? selected.map((i) => i.value) : [] }))
                                                }
                                                placeholder="Search and select employees..."
                                                closeMenuOnSelect={false}
                                            />
                                            {formData.employees.length > 0 && (
                                                <small className="text-muted mt-1 d-block">
                                                    {formData.employees.length} employee(s) selected
                                                </small>
                                            )}
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-center gap-3 mt-4 flex-wrap">
                                        <button type="submit" className={`btn px-5 ${editingMappingId ? 'btn-primary' : 'btn-primary'}`} disabled={loading}>
                                            {editingMappingId
                                                ? <><i className="fas fa-save me-2"></i>Update Mapping</>
                                                : <><i className="fas fa-link me-2"></i>Map Employees</>
                                            }
                                        </button>
                                        <button type="button" className="btn btn-outline-secondary px-4" onClick={resetForm}>
                                            <i className="fas fa-times me-1"></i>Clear
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* ── Partner-Employee Mapping Cards ── */}
                            <div className="mt-3">
                                <div className="border rounded p-4 bg-white shadow-sm">

                                    <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                                        <h4 className="mb-0" style={{ color: '#0564bf' }}>
                                            <i className="fas fa-sitemap me-2"></i>Partner–Employee Mappings
                                        </h4>
                                        <span className="badge rounded-pill px-3 py-2" style={{ background: '#0564bf', fontSize: 13 }}>
                                            {partnerGroups.length} Partner{partnerGroups.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>

                                    {partnerGroups.length === 0 ? (
                                        <div className="text-center py-5 text-muted">
                                            <i className="fas fa-link" style={{ fontSize: 48 }}></i>
                                            <p className="mt-2">No mappings found</p>
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            {partnerGroups.map((group) => {
                                                const isOpen = !!expandedPartners[group.partner_id];
                                                const isBeingEdited = editingMappingId === group.mapping_id;
                                                return (
                                                    <div className="col-12 col-md-6 col-xl-6" key={group.partner_id}>
                                                        <div
                                                            className="border rounded shadow-sm overflow-hidden"
                                                            style={{
                                                                transition: 'box-shadow 0.2s',
                                                                outline: isBeingEdited ? '2px solid #0564bf' : 'none',
                                                            }}
                                                            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(5,100,191,0.15)')}
                                                            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '')}
                                                        >
                                                            {/* Partner header */}
                                                            <div
                                                                className="d-flex align-items-center justify-content-between px-3 py-3"
                                                                style={{ background: 'linear-gradient(135deg, #0564bf, #89CFF0)', cursor: 'pointer' }}
                                                                onClick={() => togglePartner(group.partner_id)}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div
                                                                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                                        style={{
                                                                            width: 46, height: 42,
                                                                            background: 'rgba(255,255,255,0.25)',
                                                                            color: '#fff', fontWeight: 'bold', fontSize: 16,
                                                                            border: '2px solid rgba(255,255,255,0.5)',
                                                                        }}
                                                                    >
                                                                        {group.partner_full_name?.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div>
                                                                        <div className="fw-bold text-white" style={{ fontSize: 15, lineHeight: 1.2 }}>
                                                                            {group.partner_full_name}
                                                                        </div>
                                                                        <small style={{ color: 'rgba(255,255,255,0.85)' }}>
                                                                            @{group.partner_username}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span
                                                                        className="badge rounded-pill"
                                                                        style={{
                                                                            background: 'rgba(255,255,255,0.25)', color: '#fff',
                                                                            border: '1px solid rgba(255,255,255,0.5)',
                                                                            fontSize: 12, padding: '4px 10px',
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-users me-1"></i>{group.employees.length}
                                                                    </span>
                                                                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-white`} style={{ fontSize: 12 }}></i>
                                                                </div>
                                                            </div>

                                                            {/* Email strip */}
                                                            <div className="px-3 py-2 d-flex align-items-center gap-2"
                                                                style={{ background: '#f0f7ff', borderBottom: '1px solid #e0ecff' }}>
                                                                <i className="fas fa-envelope text-primary" style={{ fontSize: 11 }}></i>
                                                                <small className="text-muted text-truncate">{group.partner_email}</small>
                                                            </div>

                                                            {/* Employee list (collapsible) */}
                                                            {isOpen && (
                                                                <div className="px-3 py-2" style={{ background: '#fafcff' }}>
                                                                    {group.employees.map((emp, i) => (
                                                                        <div
                                                                            key={emp.id}
                                                                            className={`d-flex align-items-center justify-content-between py-2 ${i < group.employees.length - 1 ? 'border-bottom' : ''}`}
                                                                        >
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <div
                                                                                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                                                    style={{
                                                                                        width: 32, height: 32,
                                                                                        background: '#e8f1fb', color: '#0564bf',
                                                                                        fontWeight: 'bold', fontSize: 13,
                                                                                        border: '1px solid #c5daf5',
                                                                                    }}
                                                                                >
                                                                                    {emp.full_name?.charAt(0).toUpperCase()}
                                                                                </div>
                                                                                <div>
                                                                                    <div className="fw-semibold" style={{ fontSize: 13, color: '#1a1a2e' }}>
                                                                                        {emp.full_name}
                                                                                    </div>
                                                                                    <small className="text-muted">@{emp.username}</small>
                                                                                </div>
                                                                            </div>
                                                                            <span className={`badge ${emp.status ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: 10 }}>
                                                                                {emp.status ? 'Active' : 'Inactive'}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Footer */}
                                                            <div
                                                                className="px-3 py-2 d-flex align-items-center justify-content-between"
                                                                style={{ background: '#f0f7ff', borderTop: '1px solid #e0ecff' }}
                                                            >
                                                                <small className="text-muted">
                                                                    <i className="fas fa-user-tie me-1"></i>
                                                                    {group.employees.length} employee{group.employees.length !== 1 ? 's' : ''} assigned
                                                                </small>
                                                                <div className="d-flex gap-2">
                                                                    <button
                                                                        className="btn btn-link btn-sm p-0 text-primary"
                                                                        style={{ fontSize: 12 }}
                                                                        onClick={() => togglePartner(group.partner_id)}
                                                                    >
                                                                        {isOpen ? 'Hide' : 'View all'}
                                                                        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} ms-1`} style={{ fontSize: 10 }}></i>
                                                                    </button>
                                                                    <span className="text-muted">|</span>
                                                                    <button
                                                                        className="btn btn-link btn-sm p-0 text-primary"
                                                                        style={{ fontSize: 12 }}
                                                                        onClick={() => {
                                                                            // Find the partner in employeesList and trigger prefill
                                                                            const emp = employeesList.find((e) => e.id === group.partner_id);
                                                                            if (emp) handlePartnerCardClick(emp);
                                                                            else {
                                                                                // fallback if not in list
                                                                                setFormData({ partner: group.partner_id, employees: group.employees.map((e) => e.id) });
                                                                                setEditingMappingId(group.mapping_id);
                                                                                document.getElementById('mapping-form-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                                            }
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-edit me-1"></i>Edit
                                                                    </button>
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>

                        {/* ── Right: Partner list ── */}
                        <div className="col-12 col-lg-4 mb-4">
                            <div className="border rounded p-3 bg-white shadow-sm d-flex flex-column">
                                <h4 className="text-center mb-4" style={{ color: '#0564bf' }}>All Partners</h4>
                                <div className="mb-3">
                                    <div className="row g-2">
                                        <div className="col-12">
                                            <input type="text" name="name" placeholder="Search by name..."
                                                value={searchFilters.name} onChange={handleSearchChange}
                                                className="form-control form-control-sm" />
                                        </div>
                                        <div className="col-6">
                                            <input type="text" name="role" placeholder="Role..."
                                                value={searchFilters.role} onChange={handleSearchChange}
                                                className="form-control form-control-sm" />
                                        </div>
                                        <div className="col-6">
                                            <input type="text" name="email" placeholder="Email..."
                                                value={searchFilters.email} onChange={handleSearchChange}
                                                className="form-control form-control-sm" />
                                        </div>
                                    </div>
                                    <button className="btn btn-sm btn-primary mt-2 w-100" onClick={fetchAllEmployees}>
                                        <i className="fas fa-search me-1"></i> Search
                                    </button>
                                </div>
                                <div className="flex-grow-1 overflow-auto" style={{ maxHeight: '100vh' }}>
                                    {employeesList.filter((emp) => emp.role?.toLowerCase() === 'partner').map((emp) => {
                                        const hasMappings = !!groupedByPartner[emp.id];
                                        return (
                                            <div
                                                key={emp.id}
                                                className={`mb-3 p-3 border rounded ${formData.partner === emp.id ? 'border-primary' : ''}`}
                                                style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                                                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 2px 12px rgba(5,100,191,0.15)')}
                                                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
                                                onClick={() => handlePartnerCardClick(emp)}
                                            >
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div className="d-flex align-items-center">
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                            style={{
                                                                width: 40, height: 40,
                                                                backgroundColor: emp.gender === 'Female' ? '#e91e63' : emp.gender === 'Other' ? '#6c757d' : '#0564bf',
                                                                color: 'white', fontSize: 14, fontWeight: 'bold', flexShrink: 0,
                                                            }}
                                                        >
                                                            {emp.full_name?.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-0 fw-bold" style={{ color: '#0564bf' }}>{emp.full_name}</h6>
                                                            <small className="text-muted">ID: {emp.id} • {emp.username}</small>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex flex-column align-items-end gap-1">
                                                        <span className={`badge ${emp.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                            {emp.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                        {hasMappings && (
                                                            <span className="badge" style={{ background: '#fff3cd', color: '#856404', fontSize: 10 }}>
                                                                <i className="fas fa-users me-1"></i>
                                                                {groupedByPartner[emp.id].employees.length} mapped
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {emp.email && (
                                                    <div className="d-flex align-items-center mb-1">
                                                        <i className="fas fa-envelope me-2 text-muted" style={{ fontSize: 12 }}></i>
                                                        <small className="text-truncate">{emp.email}</small>
                                                    </div>
                                                )}
                                                <div className="d-flex justify-content-between align-items-center mt-2">
                                                    <small className="text-muted">{emp.gender}</small>
                                                    {formData.partner === emp.id ? (
                                                        <span className="badge bg-primary"><i className="fas fa-check me-1"></i>Selected</span>
                                                    ) : hasMappings ? (
                                                        <small className="text-primary fw-semibold">
                                                            <i className="fas fa-edit me-1"></i>Click to edit
                                                        </small>
                                                    ) : (
                                                        <small className="text-primary">
                                                            <i className="fas fa-mouse-pointer me-1"></i>Click to select
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {employeesList.filter((emp) => emp.role?.toLowerCase() === 'partner').length === 0 && (
                                        <div className="text-center py-4 text-muted">
                                            <i className="fas fa-users" style={{ fontSize: 48 }}></i>
                                            <p className="mt-2">No partners found</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>{/* ── end form + partner list row ── */}

                </div>
            </div>
            <DashboardFooter />
        </div>
    );
};

export default Managepartner;